import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wallet, RefreshCw, Play, CalendarPlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0));

const Row = ({ s, onAdjust, onExtend }) => {
  const [amount, setAmount] = useState("");
  return (
    <tr className="border-t border-slate-100" data-testid={`admin-sub-row-${s.id}`}>
      <td className="py-2 pr-3">
        <p className="font-semibold text-slate-900">{s.business_name || s.full_name}</p>
        <p className="text-xs text-slate-500">{(s.roles || [s.role]).filter((r) => ["partner", "artisan"].includes(r)).join(" · ")} · {s.phone || s.email}</p>
      </td>
      <td className="py-2 pr-3 font-bold" data-testid={`admin-sub-balance-${s.id}`}>{fmt(s.subscription_balance)} F</td>
      <td className="py-2 pr-3">
        <span className={`text-xs font-bold px-2 py-1 rounded-full ${s.active ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`} data-testid={`admin-sub-status-${s.id}`}>
          {s.active ? (s.trial ? "Actif · offert" : "Actif") : s.activation_pending ? "À activer" : "Suspendu"}
        </span>
      </td>
      <td className="py-2 pr-3 text-xs text-slate-600">{s.paid_until ? new Date(s.paid_until).toLocaleDateString("fr-FR") : "—"}</td>
      <td className="py-2">
        <div className="flex gap-2 items-center">
          <Input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="± FCFA" className="h-8 w-24" data-testid={`admin-sub-amount-${s.id}`} />
          <Button size="sm" onClick={() => { onAdjust(s, Number(amount)); setAmount(""); }} disabled={!Number(amount)} data-testid={`admin-sub-adjust-${s.id}`}>Appliquer</Button>
          <Button size="sm" variant="outline" onClick={() => onExtend(s)} data-testid={`admin-sub-extend-${s.id}`}><CalendarPlus className="w-3.5 h-3.5 mr-1" />+1 mois</Button>
        </div>
      </td>
    </tr>
  );
};

export default function SubscriptionsPanel() {
  const [data, setData] = useState(null);
  const [pending, setPending] = useState([]);

  const load = useCallback(async () => {
    try {
      const [r, p] = await Promise.all([
        axios.get(`${API}/admin/subscriptions`),
        axios.get(`${API}/credits/pending?type=subscription_topup`),
      ]);
      setData(r.data);
      setPending((p.data || []).filter((t) => t.type === "subscription_topup"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chargement impossible");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    try {
      await fn();
      toast.success(ok);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action impossible");
    }
  };

  const onAdjust = (s, amount) => act(() => axios.post(`${API}/admin/subscriptions/${s.id}/adjust`, { amount, note: "Ajustement Tour de Contrôle" }), "Solde mis à jour");
  const onExtend = (s) => act(() => axios.post(`${API}/admin/subscriptions/${s.id}/extend`, { months: 1 }), "Abonnement prolongé d'un mois");
  const runNow = () => act(() => axios.post(`${API}/admin/subscriptions/run`), "Prélèvements et alertes exécutés");

  return (
    <Card data-testid="admin-subscriptions-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
        <CardTitle className="flex items-center gap-2"><Wallet className="w-5 h-5" /> Abonnements commerçants & artisans</CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="admin-sub-refresh"><RefreshCw className="w-4 h-4 mr-1" />Actualiser</Button>
          <Button size="sm" onClick={runNow} data-testid="admin-sub-run"><Play className="w-4 h-4 mr-1" />Lancer les prélèvements</Button>
        </div>
      </CardHeader>
      <CardContent>
        {pending.length > 0 && (
          <div className="mb-5 border border-amber-200 bg-amber-50 rounded-xl p-3" data-testid="admin-sub-pending">
            <p className="text-xs font-bold uppercase text-amber-800 mb-2">Recharges à vérifier ({pending.length})</p>
            {pending.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 py-1.5 text-sm" data-testid={`admin-sub-pending-${t.id}`}>
                <span>{t.user_name} · {t.payment_method === "wave" ? "Wave" : "Orange Money"} {t.payment_phone || ""} · <b>{fmt(t.amount)} F</b></span>
                <Button size="sm" onClick={() => act(() => axios.post(`${API}/credits/confirm/${t.id}`), "Paiement confirmé")} data-testid={`admin-sub-confirm-${t.id}`}>Confirmer</Button>
              </div>
            ))}
          </div>
        )}
        {data && (
          <div className="flex gap-4 mb-4 text-sm" data-testid="admin-sub-totals">
            <span>Total : <b>{data.total}</b></span>
            <span className="text-emerald-700">Actifs : <b>{data.active}</b></span>
            <span className="text-rose-700">Suspendus : <b>{data.suspended}</b></span>
            <span className="text-slate-500">Tarif : {fmt(data.fee)} FCFA / mois</span>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500">
                <th className="pb-2">Compte</th><th className="pb-2">Solde</th><th className="pb-2">Statut</th><th className="pb-2">Visible jusqu'au</th><th className="pb-2">Gérer</th>
              </tr>
            </thead>
            <tbody>
              {(data?.subscribers || []).map((s) => <Row key={s.id} s={s} onAdjust={onAdjust} onExtend={onExtend} />)}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
